import { inject, Injectable } from "@angular/core";
import type { ScopedProject } from "@domain/project/project-guards";
import { TemplateDto, TemplateInformationFieldDto, TemplatePropertyDto } from "@domain/project/template";
import { TemplateService } from "@domain/project/template/template.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { validateSync, ValidationError } from "class-validator";
import { catchError, map, of, switchMap, take, tap, throwError } from "rxjs";

type PartialTemplate = {
  name?: string;
  supportedCountries?: string[];
  properties?: TemplatePropertyDto;
};

@Injectable({ providedIn: "root" })
export class ProjectValidationService {
  private templateService: TemplateService = inject(TemplateService);

  validateProject(project: ScopedProject, userScope: BusinessScope) {
    try {
      this.validateAgainstProject(project);
    } catch (err) {
      return throwError(() => err);
    }

    return this.templateService.projectTemplate$.pipe(
      take(1),
      switchMap((template) => {
        if (template) return of(template);

        return this.templateService.generateTemplate(userScope, project.country).pipe(map((raw) => this.normalizeTemplate(raw)));
      }),
      tap((template: TemplateDto) => {
        this.validateAgainstTemplate(project, template);
      }),
      map(() => project),
      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }

  private normalizeTemplate(raw?: PartialTemplate): TemplateDto {
    if (!raw) {
      throw new Error("Template is undefined");
    }
    return {
      name: raw.name ?? "",
      supportedCountries: raw.supportedCountries ?? [],
      properties: raw.properties ?? ({} as TemplatePropertyDto),
    };
  }

  private validateAgainstTemplate(project: ScopedProject, template: TemplateDto) {
    const projectDetails = template.properties?.project?.details;
    if (!projectDetails) return;

    const detailsRecord = Object.entries(projectDetails) as [string, TemplateInformationFieldDto][];

    const projectRecord = project as unknown as Record<string, unknown>;
    for (const [fieldKey, field] of detailsRecord) {
      const value = projectRecord[fieldKey];

      if (value === undefined || value === null) {
        if (field.visible) {
          this.throwValidationError("Missing visible field", fieldKey, value, project);
        }
        if (field.required) {
          this.throwValidationError("Missing required field", fieldKey, value, project);
        }
      }
      this.validateType(value, field, fieldKey);
    }
  }

  private validateAgainstProject(project: ScopedProject) {
    const baseErrors = validateSync(project);
    if (baseErrors.length > 0) {
      console.log(baseErrors);
      const messages = `ScopedProject validation failed: ${this.formatErrors(baseErrors)}`;
      console.error(messages);
      throw new Error(messages);
    }
    return project;
  }

  private validateType(value: unknown, field: TemplateInformationFieldDto, fieldKey: string) {
    switch (field.type) {
      case "string":
        if (typeof value !== "string") {
          const messages = `Field "${fieldKey}" must be a string`;
          console.error(messages);
          throw new Error(messages);
        }
        break;
      case "number":
        if (typeof value !== "number") {
          const messages = `Field "${fieldKey}" must be a number`;
          console.error(messages);
          throw new Error(messages);
        }
        break;
      case "boolean":
        if (typeof value !== "boolean") {
          const messages = `Field "${fieldKey}" must be a boolean`;
          console.error(messages);
          throw new Error(messages);
        }
        break;
    }
  }

  private throwValidationError(reason: string, fieldKey: string, value: unknown, project: ScopedProject): never {
    const message = `${reason}: "${fieldKey}" in project "${project.id ?? "unknown"}". Received: ${value}`;
    console.error(message, { fieldKey, value, project });
    throw new Error(message);
  }

  private formatErrors(errors: ValidationError[]): string[] {
    const messages: string[] = [];
    errors.forEach((err) => {
      const constraints = err?.constraints as Record<string, string> | undefined;
      messages.push(...Object.values(constraints ?? {}));
    });
    return messages;
  }
}
